import React from "react";
import PropTypes from "prop-types";
import gql from "graphql-tag";
import isEmpty from "lodash/isEmpty";

import range from "shared/util/range";
import noop from "shared/util/noop";
import Paginate from "shared/components/Paginate";
import { isMobile } from "shared/util/EnvironmentHelpers";

import {
	PLACEHOLDER_COUNT_MESSAGES,
	MESSAGE_FOLDERS,
} from "apps/messages/util/constants";

import Section from "apps/messages/components/Section";
import MessagesBlankState from "apps/messages/components/MessagesBlankState";
import MessageRow, { MobileMessageRow } from "apps/messages/components/MessageRow";
import MessageRowPlaceholder from "apps/messages/components/MessageRowPlaceholder";

const MessageRowComponent = isMobile ? MobileMessageRow : MessageRow;

const Messages = ({
	title,
	conversations,
	filter,
	fetchMore,
	markConversationRead,
	loading,
	blankStateContent,
	hiddenUsers,
	picBorderWidth,
	onMessageOpen,
	hasInfiniteScroll,
}) => {
	const { pageInfo = {} } = conversations;
	let hasConversations = false;
	let messagesContent;

	const handleFetchMore = () => {
		if (!pageInfo.after || !pageInfo.hasMore || !hasInfiniteScroll) {
			return;
		}

		fetchMore({
			variables: {
				conversationsFilter: filter,
				conversationsAfter: conversations.pageInfo.after,
			},
			updateQuery: (prev, { fetchMoreResult }) => {
				if (!fetchMoreResult) {
					return prev;
				}

				return {
					...fetchMoreResult,
					user: {
						...fetchMoreResult.user,
						conversations: {
							...fetchMoreResult.user.conversations,
							data: [
								...prev.user.conversations.data,
								...fetchMoreResult.user.conversations.data,
							],
						},
					},
				};
			},
		});
	};

	if (loading && isEmpty(conversations)) {
		messagesContent = range(0, PLACEHOLDER_COUNT_MESSAGES).map((num) => (
			<MessageRowPlaceholder key={`placeholder-${num}`} />
		));
	} else {
		const filteredConversations = conversations.data
			.filter((conversation) => {
				const { correspondent = {} } = conversation;
				const { user = {} } = correspondent;

				return !(user.id && hiddenUsers[user.id]);
			});

		hasConversations = !!filteredConversations.length;

		messagesContent = (
			<Paginate
				loading={loading}
				hasMore={pageInfo.hasMore}
				loadMore={handleFetchMore}
			>
				{filteredConversations.map((conversation) => (
					<MessageRowComponent
						key={conversation.threadid}
						snippet={conversation.snippet.text}
						timestamp={conversation.time}
						threadid={conversation.threadid}
						sentTime={conversation.sentTime}
						receivedTime={conversation.receivedTime}
						isUnread={conversation.isUnread}
						isInactive={filter === MESSAGE_FOLDERS.ARCHIVED}
						correspondent={conversation.correspondent}
						inboxSize={conversations.pageInfo.total}
						picBorderWidth={picBorderWidth}
						markConversationRead={markConversationRead}
						onClick={onMessageOpen}
					/>
				))}
			</Paginate>
		);
	}

	return (
		<Section
			className="messages-messagesection"
			header={title}
		>
			<div className="messages-messagesection-content">
				<div className="messages-section-messagerows" ref={this._rowsContainer}>
					{messagesContent}
				</div>
				{!hasConversations && !loading &&
					<MessagesBlankState {...blankStateContent} />
				}
			</div>
		</Section>
	);
};

// Note: this handles initial requests + pagination, but the page
// is kept updated using InstantEvents (see: InstantEventSubscriber.js).
// Whenever this request is updated, be sure to update that file, too.
Messages.fragments = {
	conversations: gql`
		fragment Conversations on User {
			notificationCounts {
				messages
			}
			conversations(filter: $conversationsFilter, after: $conversationsAfter) {
				data {
					threadid
					time
					isUnread
					sentTime
					receivedTime
					correspondent {
						senderLikeTime
						targetLikeTime
						targetLikeViaSpotlight
						senderMessageTime
						targetMessageTime
						matchPercent
						user {
							id
							displayname
							username
							age
							isOnline
							primaryImage {
								square225
							}
						}
					}
					snippet {
						text
						sender {
							id
						}
					}
				}
				pageInfo {
					hasMore
					after
					total
				}
			}
		}
	`,
};

Messages.propTypes = {
	filter: PropTypes.string.isRequired,
	fetchMore: PropTypes.func.isRequired,
	title: PropTypes.string,
	conversations: PropTypes.object,
	blankStateContent: PropTypes.object,
	hiddenUsers: PropTypes.object,
	loading: PropTypes.bool,
	hasInfiniteScroll: PropTypes.bool,
	picBorderWidth: PropTypes.number,
	markConversationRead: PropTypes.func,
	onMessageOpen: PropTypes.func,
};

Messages.defaultProps = {
	title: null,
	conversations: {},
	blankStateContent: undefined,
	hiddenUsers: {},
	loading: true,
	hasInfiniteScroll: true,
	picBorderWidth: process.env.PLATFORM === "mobile" ? 3 : 4,
	markConversationRead: noop,
	onMessageOpen: noop,
};

export default Messages;



// WEBPACK FOOTER //
// ./src/apps/messages/components/Messages.jsx